/**
 * 内容页浅/深主题全局上下文（M4 修复：data-theme 泄入小镇首页）。
 *
 * 原型语义（site.js themeToggle）：主题切换按钮只存在于内容页页头，且
 * `if (!buttons.length) return` —— 小镇首页**不 apply** 任何主题（全屏图片场景
 * 自带昼夜切换，恒深色 token）。工程版原先把 data-theme 放在 SiteHeader 的
 * local state —— SPA 客户端导航回首页后 SiteHeader 卸载、属性残留在 <html>，
 * Light token 泄入首页：深底控件（rgba(16,19,18,.86)）的字色变 Light --ink
 * 深蓝 #2e4650 →「深蓝字 + 非白背景」不可辨。
 *
 * 修复 = 主题提升为 Provider（router 布局层挂载），**按路由区分**：
 * - 内容页（有 SiteHeader 的页面）→ 按偏好 apply data-theme；
 * - 小镇首页（/ 与 /{locale} 根）→ 强制移除 data-theme（原型 guard 等价语义）。
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router';
import { readThemePreference, writeThemePreference } from '../components/SiteHeader';

interface ContentThemeContextValue {
  light: boolean;
  toggleTheme: () => void;
}

const ContentThemeContext = createContext<ContentThemeContextValue | null>(null);

/** 小镇首页路由（此路径恒深色 token，不参与内容页主题） */
function isTownRoot(pathname: string): boolean {
  // /、/zh-CN、/en（允许尾斜杠）； town 场景日后加深链也不含子段
  return /^\/(?:zh-CN|en)?\/?$/.test(pathname);
}

export function ContentThemeProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [light, setLight] = useState(readThemePreference);

  const toggleTheme = useCallback(() => {
    setLight((prev) => {
      const next = !prev;
      writeThemePreference(next);
      return next;
    });
  }, []);

  // 主题应用（原型 themeToggle apply 语义 + 首页 guard）：
  // 首页强制深色 token；内容页按偏好。无论哪种，都要保证属性与当前路由一致
  //（跨页导航时清除上一页残留——原缺陷）。
  useEffect(() => {
    if (isTownRoot(location.pathname)) {
      document.documentElement.removeAttribute('data-theme');
    } else if (light) {
      document.documentElement.setAttribute('data-theme', 'light');
    } else {
      document.documentElement.removeAttribute('data-theme');
    }
  }, [location.pathname, light]);

  const value = useMemo(() => ({ light, toggleTheme }), [light, toggleTheme]);
  return <ContentThemeContext.Provider value={value}>{children}</ContentThemeContext.Provider>;
}

export function useContentTheme(): ContentThemeContextValue {
  const ctx = useContext(ContentThemeContext);
  if (!ctx) throw new Error('useContentTheme 必须在 ContentThemeProvider 内使用');
  return ctx;
}
